import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';

import type { Moment } from 'moment';

const DAY_KEY_FORMAT = 'YYYY-MM-DD';

const getTodayKey = (): string => moment().format(DAY_KEY_FORMAT);

// Start of the current calendar day that follows the clock in a tab that is
// never reloaded. Two triggers are required: a timer to the nearest midnight
// covers a tab left open and visible, while visibilitychange/focus cover
// background tabs, where browsers throttle timers indefinitely (same approach
// as useLeadsRegistryDateRollover). The moment identity changes only when the
// day does, so it is safe as a memo/effect dependency.
export const useTodayMoment = (): Moment => {
  const [todayKey, setTodayKey] = useState(getTodayKey);

  useEffect(() => {
    let timerId: number | undefined;

    const syncTodayHandler = () => {
      setTodayKey(getTodayKey());
    };

    const scheduleMidnightTick = () => {
      const now = moment();
      // The extra second guards against the timer firing a moment before
      // midnight due to clock rounding and resolving the old day again.
      const delay = now.clone().add(1, 'day').startOf('day').diff(now) + 1_000;
      timerId = window.setTimeout(() => {
        syncTodayHandler();
        scheduleMidnightTick();
      }, delay);
    };

    scheduleMidnightTick();
    document.addEventListener('visibilitychange', syncTodayHandler);
    window.addEventListener('focus', syncTodayHandler);

    return () => {
      window.clearTimeout(timerId);
      document.removeEventListener('visibilitychange', syncTodayHandler);
      window.removeEventListener('focus', syncTodayHandler);
    };
  }, []);

  return useMemo(
    () => moment(todayKey, DAY_KEY_FORMAT).startOf('day'),
    [todayKey]
  );
};
